import { colorIndexFromName, userColor } from "./colors";
import {
  ACTIVITY_LEVEL_LABEL,
  activityLevel,
  cellActivity,
  formatAge,
  formatCellAge,
  formatScore,
  RECENCY_LABEL,
  type RankedUser,
  type ViewportSummary,
} from "./stats";
import { OSM_ZOOM_FROM_MAPLIBRE } from "./permalink";
import type { ActivityCenter, CellView, FilterId, UserStat, ViewMode } from "./types";
import { FILTER_LABELS, SPECIALTY_COLORS, SPECIALTY_LABELS } from "./types";

/** Shown while cells.bin.gz is still in flight; the map itself is already usable. */
const LOADING_USERS = "Mapper:innen werden geladen…";

const TIP = {
  objects: "Anzahl der Objekte in diesem Gitterfeld für den gewählten Filter.",
  activity:
    "Aktivität der Mapper:innen in diesem Gitterfeld: jüngere Bearbeitungen zählen stärker, schwach kartierte Felder werden heruntergewichtet.",
  scoreSmooth:
    "Geglätteter Aktivitätswert der Mapper:in – zählt Edits in diesem Gitterfeld und den Nachbarfeldern. Neuere Bearbeitungen zählen stärker. Dieser Wert bestimmt, wer als aktivste Mapper:in in einem Gebiet gilt.",
  scoreCell:
    "Tatsächlicher Aktivitätswert der Mapper:in nur in diesem Gitterfeld, ohne die Nachbarn. Neuere Bearbeitungen zählen stärker.",
  scoreView:
    "Summe der Aktivitäten der Mapper:in in den sichtbaren Gitterfeldern, ohne Glättung mit Nachbarn. Neuere Bearbeitungen zählen stärker. Eine Kartenbearbeitung der letzten 6 Monate zählt 1 Punkt; ältere Bearbeitungen weniger (bis nahe 0 bei Änderungen, die schon einige Jahre her sind). Liegt ein Objekt in mehreren Gitterfeldern, wird der Punktwert geteilt.",
  center: "Gitterfeld mit dem höchsten geglätteten Punktwert der Mapper:in.",
  fieldList: "Mapper:in mit dem höchsten Aktivitäts-Score in diesem Gitterfeld, basierend darauf, wer ein OSM-Objekt zuletzt bearbeitet hat.",
  leader: "Mapper:in mit dem höchsten geglätteten Aktivitäts-Score in diesem Gitterfeld und der Umgebung (aktivste Mapper:in in der Gegend).",
  flags:
    "Diese Mapper:innen haben in diesem Gitterfeld ein Aktivitätszentrum (Fähnchen auf der Karte), sind aber nicht die aktivsten Mapper:innen im Umkreis.",
  viewList: "Top-10-Mapper:innen mit den höchsten Aktivitäts-Scores im sichtbaren Kartenausschnitt, basierend darauf, wer ein OSM-Objekt zuletzt bearbeitet hat.",
  newcomers:
    "Mapper:innen, deren ungeglätteter Last-Editor-Score (Filter Alle, ohne Nachbarfelder) zu mindestens 90 % aus den 12 Monaten vor dem Stichtag stammt, mit mindestens 25 Last-Editor-Objekten in diesem Fenster. Im Ausschnitt erscheinen sie nur, wenn sie dort in den letzten 12 Monaten zuletzt aktiv waren. Die Zahl vor dem Namen ist der Rang in der Gesamtliste des Ausschnitts.",
  summary:
    "Anteil der Gitterfelder im Ausschnitt mit hoher oder sehr hoher Mapping-Aktivität.",
  osmExtent: "Denselben Kartenausschnitt auf openstreetmap.org öffnen.",
};

function tip(label: string, text: string): string {
  return `<span class="tip" title="${escapeHtml(text)}">${label}</span>`;
}

function capitalSvg(): string {
  return `<svg class="capital" viewBox="0 0 64 64" width="14" height="14" aria-hidden="true">
    <circle cx="32" cy="32" r="10" fill="#111111"/>
    <circle cx="32" cy="32" r="18.5" fill="none" stroke="#111111" stroke-width="7"/>
  </svg>`;
}

function osmUserUrl(name: string): string {
  return `https://www.openstreetmap.org/user/${encodeURIComponent(name)}`;
}

function osmExtIcon(): string {
  return `<svg class="osm-ext-icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
    <path fill="currentColor" d="M6.5 3h-3A1.5 1.5 0 0 0 2 4.5v8A1.5 1.5 0 0 0 3.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-3h-1.25v3a.25.25 0 0 1-.25.25h-8a.25.25 0 0 1-.25-.25v-8a.25.25 0 0 1 .25-.25h3V3Zm4.25 0H14v3.25h-1.25V5.13L8.06 9.81l-.87-.87 4.69-4.69H10.75V3Z"/>
  </svg>`;
}

function osmProfileLink(name: string): string {
  if (!name || name.startsWith("#")) return "";
  return `<a class="osm-ext" href="${osmUserUrl(name)}" target="_blank" rel="noopener noreferrer" title="OSM-Profil von ${escapeHtml(name)}" aria-label="OSM-Profil von ${escapeHtml(name)} öffnen">${osmExtIcon()}</a>`;
}

function countNoun(filter: FilterId): string {
  return filter === "notes" ? "Notes" : "Features";
}

function specialtyIcon(key: keyof typeof SPECIALTY_LABELS): string {
  if (key === "highway") {
    return `<svg class="stack-ico" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path fill="#fff" fill-rule="evenodd" d="M3.4 14.6 6.4 1.6h3.2l3 13H3.4zm4.35-11.5-.4 11.5h1.3l.4-11.5z"/></svg>`;
  }
  if (key === "building") {
    return `<svg class="stack-ico" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path fill="#fff" fill-rule="evenodd" d="M8 1.4 14.5 7.35V14.6H1.5V7.35L8 1.4zM6.45 10.2h3.1V14.6h-3.1z"/></svg>`;
  }
  if (key === "landuse") {
    return `<svg class="stack-ico" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><circle cx="8" cy="5.15" r="3.55" fill="#fff"/><circle cx="5.15" cy="7.35" r="3.05" fill="#fff"/><circle cx="10.85" cy="7.35" r="3.05" fill="#fff"/><rect x="6.95" y="10" width="2.1" height="4.7" rx="0.4" fill="#fff"/></svg>`;
  }
  if (key === "furniture") {
    return `<svg class="stack-ico" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path fill="#fff" d="M2 7.4h12v1.7H2z"/><rect x="3.4" y="9.1" width="1.6" height="4.2" rx="0.3" fill="#fff"/><rect x="11" y="9.1" width="1.6" height="4.2" rx="0.3" fill="#fff"/></svg>`;
  }
  return `<svg class="stack-ico" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true"><path fill="#fff" fill-rule="evenodd" d="M8 1.15a5.15 5.15 0 0 0-5.15 5.15c0 3.55 5.15 8.55 5.15 8.55s5.15-5 5.15-8.55A5.15 5.15 0 0 0 8 1.15zM8 4.05a2.2 2.2 0 1 1 0 4.4 2.2 2.2 0 0 1 0-4.4z"/></svg>`;
}

function specialtyStack(spec: UserStat["specialties"]): string {
  const keys = Object.keys(SPECIALTY_LABELS) as Array<keyof typeof SPECIALTY_LABELS>;
  const segs = keys
    .map((key) => ({ key, pct: Math.round((spec[key] ?? 0) * 100) }))
    .filter((s) => s.pct > 0);
  if (!segs.length) return "";
  const parts = segs
    .map((s) => {
      const label = SPECIALTY_LABELS[s.key];
      const color = SPECIALTY_COLORS[s.key];
      return `<span class="stack-seg" style="width:${s.pct}%;background:${color}" title="${escapeHtml(label)}">${specialtyIcon(s.key)}</span>`;
    })
    .join("");
  return `<div class="stack" role="img" aria-label="Themenanteile">${parts}</div>`;
}

function userLink(uid: number, name: string, selectedUids: Set<number>): string {
  const on = selectedUids.has(uid);
  return `<span class="user-name">${osmProfileLink(name)}<button type="button" class="user-link${on ? " on" : ""}" data-uid="${uid}" aria-pressed="${on ? "true" : "false"}">${escapeHtml(name)}</button></span>`;
}

function userRow(
  entry: { uid?: number; name: string; score: number; lastTs?: number; colorIndex?: number },
  rank: number,
  selectedUids: Set<number>,
  scoreTip = TIP.scoreCell,
  asOf?: number | null,
): string {
  const swatch = userColor(entry.colorIndex ?? colorIndexFromName(entry.name));
  const age =
    asOf != null && entry.lastTs ? `<small>${formatCellAge(entry.lastTs, asOf)}</small>` : "";
  const name = entry.uid != null ? userLink(entry.uid, entry.name, selectedUids) : escapeHtml(entry.name);
  return `<li><b>${rank}.</b> <i style="background:${swatch}"></i> <span>${name}</span> <em class="tip" title="${escapeHtml(scoreTip)}">${formatScore(entry.score)}</em> ${age}</li>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]!);
}

function pennantSvg(color: string): string {
  return `<svg class="pennant" viewBox="0 0 48 60" width="14" height="17" aria-hidden="true">
    <path d="M10 4 L42 16 L10 28 Z" fill="${color}" stroke="#2a1c12" stroke-width="1.5" stroke-linejoin="round" />
    <line x1="10" y1="2" x2="10" y2="58" stroke="#2a1c12" stroke-width="3" />
  </svg>`;
}

function activityPhrase(value: number): string {
  return ACTIVITY_LEVEL_LABEL[activityLevel(value)];
}

/**
 * MapLibre-Zoom nach OSM.org (Leaflet) umrechnen.
 *
 * Leaflet/OSM.org: Zoom 0 = die Welt ist 256 CSS-Pixel breit (Rasterkacheln).
 * MapLibre:        Zoom 0 = die Welt ist 512 CSS-Pixel breit (Vektorkacheln).
 * Derselbe z-Wert ist in MapLibre daher eine Zoomstufe näher; +1 gleicht das aus.
 */
export function osmExtentUrl(center: { lng: number; lat: number }, zoom: number): string {
  const z = Math.max(0, Math.min(19, Math.round(zoom + OSM_ZOOM_FROM_MAPLIBRE)));
  return `https://www.openstreetmap.org/#map=${z}/${center.lat.toFixed(5)}/${center.lng.toFixed(5)}`;
}

export function renderCellPanel(
  el: HTMLElement,
  view: CellView | null,
  users: Record<string, UserStat>,
  filter: FilterId,
  centers: ActivityCenter[] = [],
  _mode: ViewMode = "users",
  colorByUid?: Map<number, number>,
  selectedUids: Set<number> = new Set(),
  threshold = 20,
  asOf: number | null = null,
): void {
  if (!view) {
    el.innerHTML = `<p class="hint">Klicke ein Gitterfeld, um dort aktive Mapper:innen zu sehen.</p>`;
    return;
  }
  const winner = view.winner ? users[String(view.winner)] : null;
  const title = view.sparse
    ? "Geringe Aktivität"
    : winner
      ? userLink(view.winner, winner.name, selectedUids)
      : "Ohne Gewinner";
  const here = centers.filter((c) => c.h3 === view.h3);
  const isActivityCenter = here.some((c) => c.own === 1);
  const flagRows = here
    .filter((c) => c.own !== 1)
    .map((c) => {
      const name = users[String(c.uid)]?.name ?? `#${c.uid}`;
      const color = userColor(colorByUid?.get(c.uid) ?? colorIndexFromName(name));
      const on = selectedUids.has(c.uid);
      const flagTip = `Auch ${name} ist hier aktiv und hat hier ein Aktivitätszentrum.`;
      return `<p class="flag-user" title="${escapeHtml(flagTip)}"><span class="user-name">${osmProfileLink(name)}<button type="button" class="user-link${on ? " on" : ""}" data-uid="${c.uid}" aria-pressed="${on ? "true" : "false"}" title="${escapeHtml(flagTip)}">${pennantSvg(color)}<span>${escapeHtml(name)}</span></button></span></p>`;
    })
    .join("");
  const flagBlock = flagRows
    ? `<h3 class="quiet-head">${tip("Weitere Aktivitätszentren", TIP.flags)}</h3>${flagRows}`
    : "";
  const top5 = (view.top ?? []).slice(0, 5).map((row, i) => {
    const u = users[String(row.uid)];
    return userRow(
      {
        uid: row.uid,
        name: u?.name ?? `#${row.uid}`,
        score: row.score,
        lastTs: row.lastTs,
        colorIndex: colorByUid?.get(row.uid) ?? (u ? colorIndexFromName(u.name) : view.colorIndex),
      },
      i + 1,
      selectedUids,
      TIP.scoreCell,
      asOf,
    );
  });
  const activity = activityPhrase(cellActivity(view, threshold));
  const centerMark = isActivityCenter
    ? `<p class="center-mark">${capitalSvg()}${tip("Aktivitätszentrum", TIP.center)}</p>`
    : "";
  const leaderScore = view.sparse
    ? ""
    : `<em class="tip" title="${escapeHtml(TIP.scoreSmooth)}">${formatScore(view.score)}</em>`;
  const sparseNote = view.sparse
    ? `<p class="score">Gitterfeld mit gegenwärtig sehr geringer Mappingaktivität</p>`
    : "";
  const kicker = `<p class="kicker">${FILTER_LABELS[filter]} · <span class="tip" title="${escapeHtml(TIP.objects)}">${view.count.toLocaleString("de-DE")} ${countNoun(filter)}</span> · <span class="tip" title="${escapeHtml(TIP.activity)}">${activity}</span></p>`;
  el.innerHTML = `
    <h3 class="cell-list-head quiet-head">${tip("Aktivste Mapper:in in der Gegend", TIP.leader)}<button type="button" class="cell-clear" data-clear-cell="1" aria-label="Gitterfeld abwählen" title="Gitterfeld abwählen">×</button></h3>
    <div class="leader">
      <h2>${title}</h2>
      ${leaderScore}
    </div>
    ${centerMark}
    ${sparseNote}
    ${flagBlock}
    <h3>${tip("Aktivste Mapper:innen im Gitterfeld", TIP.fieldList)}</h3>
    ${kicker}
    <ol class="rank">${top5.join("") || `<li${view.top ? "" : ' class="empty"'}>${view.top ? "Keine Edits" : LOADING_USERS}</li>`}</ol>
  `;
}

export function renderHighlightChip(
  el: HTMLElement,
  uids: Set<number>,
  users: Record<string, UserStat>,
): void {
  if (!uids.size) {
    el.innerHTML = "";
    el.classList.add("hide");
    el.hidden = true;
    return;
  }
  el.hidden = false;
  el.classList.remove("hide");
  const chips = [...uids]
    .map((uid) => {
      const name = users[String(uid)]?.name ?? `#${uid}`;
      return `<span class="chip-user"><strong>${escapeHtml(name)}</strong><button type="button" class="user-clear" data-uid="${uid}" aria-label="${escapeHtml(name)} abwählen" title="Abwählen">×</button></span>`;
    })
    .join("");
  const clearAll =
    uids.size > 1
      ? `<button type="button" class="user-clear-all" data-clear-all="1" aria-label="Auswahl aufheben" title="Alle abwählen">alle aufheben</button>`
      : "";
  el.innerHTML = `
    <div class="user-chip-head">
      <p>Auswahl</p>
      ${clearAll}
    </div>
    <div class="user-chip-list">${chips}</div>
  `;
}

export function renderViewportPanel(
  el: HTMLElement,
  ranked: RankedUser[] | null,
  summary: ViewportSummary,
  _colorByUid?: Map<number, number>,
  selectedUids: Set<number> = new Set(),
  osmUrl = "https://www.openstreetmap.org/",
  asOf: number | null = null,
  filter: FilterId = "all",
): void {
  const rows = ranked ?? [];
  const top = rows.slice(0, 10);
  const placeholder = (text: string) =>
    ranked ? `<li class='empty'>${text}</li>` : `<li class='empty'>${LOADING_USERS}</li>`;
  const recencyLine = (u: RankedUser): string => {
    if (asOf == null) return "";
    if (u.recency === 1) return `<p class="muted">${RECENCY_LABEL[1]}</p>`;
    return `<p class="muted">${RECENCY_LABEL[u.recency]} (${formatAge(u.lastTs, asOf)})</p>`;
  };
  const list = (items: RankedUser[], empty: string) =>
    items
      .map((u) => {
        return `<li>
          <header><b>${u.rank}.</b> <span>${userLink(u.uid, u.name, selectedUids)}</span> <em class="tip" title="${escapeHtml(TIP.scoreView)}">${formatScore(u.score)}</em></header>
          ${recencyLine(u)}
          ${specialtyStack(u.specialties)}
        </li>`;
      })
      .join("") || placeholder(empty);
  const newcomers = rows.filter((u) => u.newcomer).slice(0, 10);

  const noun = countNoun(filter);
  const highShare =
    summary.highShare == null
      ? `? % der Gitterfelder mit (sehr) hoher Aktivität`
      : `${Math.round(summary.highShare * 100).toLocaleString("de-DE")} % der Gitterfelder mit (sehr) hoher Aktivität`;
  const mappers =
    summary.mappers == null
      ? "? Mapper:innen"
      : summary.mappers === 1
        ? "1 Mapper:in"
        : `${summary.mappers.toLocaleString("de-DE")} Mapper:innen`;
  const objects =
    summary.objects == null
      ? `? ${noun}`
      : `${summary.objects.toLocaleString("de-DE")} ${noun}`;
  el.innerHTML = `
    <p class="summary">${mappers} · ${objects} · <span class="tip" title="${escapeHtml(TIP.summary)}">${highShare}</span></p>
    <h3 class="viewport-list-head">${tip("Aktivste Mapper:innen im Kartenausschnitt", TIP.viewList)}<a class="osm-ext osm-extent" href="${escapeHtml(osmUrl)}" target="_blank" rel="noopener noreferrer" title="${escapeHtml(TIP.osmExtent)}" aria-label="${escapeHtml(TIP.osmExtent)}"><img class="osm-logo" src="./osm-logo.svg" width="22" height="22" alt=""></a></h3>
    <ol class="rank fat">${list(top, "Keine Mapper:innen im Ausschnitt")}</ol>
    <h3 class="viewport-list-head viewport-newcomers-head">${tip("Newcomer im Kartenausschnitt", TIP.newcomers)}</h3>
    <ol class="rank fat">${list(newcomers, "Keine Newcomer im Ausschnitt")}</ol>
  `;
}
